import {
  parseJsonifiedArray,
  parsePlusSeparatedNumbers,
} from '@efdigital/ll-addon-content';
import {
  type Answer,
  type AnswerValue,
  type Quiz,
  type QuizMarkLike,
  type QuizMarkScoreLike,
  type QuizMarkValueLike,
  type QuizQuestion,
  type QuizQuestionChoice,
  type QuizQuestionPoints,
  type QuizQuestionRank,
  type QuizQuestionText,
  QuizQuestionType,
  type QuizResponseLike,
} from '@efdigital/ll-addon-data';

const isCategory = (
  item: { category: string; additionalCategories?: { category: string }[] },
  expected: string
) => {
  if (!expected) {
    return false;
  }

  if (!item) {
    return false;
  }

  if (item.category === expected) {
    return true;
  }

  if (
    Array.isArray(item.additionalCategories) &&
    item.additionalCategories?.some((c) => c.category === expected)
  ) {
    return true;
  }
  return false;
};

const isCategoryOrParentCategory = (
  item: { category: string; additionalCategories?: { category: string }[] },
  expected: string,
  expectedParent?: string
) => {
  if (expectedParent) {
    return isCategory(item, expected) || isCategory(item, expectedParent);
  } else {
    return isCategory(item, expected);
  }
};

export class Marker {
  markQuestionHidden(answer: AnswerValue): QuizMarkValueLike {
    return {
      qid: answer.qid,
      score: 0,
      scores: [],
      responses: [],
      answered: true,
      correct: true,
    };
  }

  markQuestionMeta(answer: AnswerValue): QuizMarkValueLike {
    return {
      qid: answer.qid,
      score: 0,
      scores: [],
      responses: [],
      answered: true,
      correct: true,
    };
  }

  markQuestionText(
    textFragment: QuizQuestionText,
    answer: AnswerValue
  ): QuizMarkValueLike {
    const mark = {
      qid: answer.qid,
      score: 0,
      scores: [] as QuizMarkScoreLike[],
      responses: [] as QuizResponseLike[],
      answered: false,
      correct: false,
    };

    if (answer && answer.value) {
      mark.answered = true;
      mark.correct = true;
      mark.score = textFragment.score || 0;
      if (textFragment.category) {
        mark.scores = [
          {
            category: textFragment.category,
            score: textFragment.score || 0,
            raw: 0,
            ilow: 0,
            ihigh: 0,
          },
        ];
      }
      if (textFragment.response) {
        mark.responses.push(textFragment.response as QuizResponseLike);
      }
    } else {
      if (textFragment.response0) {
        mark.responses.push(textFragment.response0 as QuizResponseLike);
      }
    }

    return mark;
  }

  markQuestionChoice(
    choiceFragment: QuizQuestionChoice,
    answer: AnswerValue
  ): QuizMarkValueLike {
    const mark = {
      qid: answer.qid,
      score: 0,
      scores: [] as QuizMarkScoreLike[],
      responses: [] as QuizResponseLike[],
      answered: false,
      correct: false,
    };

    if (!answer.value || answer.value == '[]') {
      return mark;
    }

    const choices = choiceFragment.choices;
    const scoring = choiceFragment.scoring || 'exact';
    const type = choiceFragment.type || 'single';
    const values: string[] =
      type == 'multiple'
        ? parseJsonifiedArray(answer.value)
        : [answer.value as string];

    if (scoring == 'exact') {
      let correct = false;

      if (type == 'multiple') {
        correct = true;
        choices.forEach((choice) => {
          if (choice.correct) {
            correct =
              correct &&
              values.some((v) => v === choice.oid || v === choice.value);
          } else {
            correct =
              correct &&
              !values.some((v) => v === choice.oid || v === choice.value);
          }
        });
      } else {
        const choice = choices.find(
          (c) => c.oid === answer.value || c.value === answer.value
        );
        correct = (choice && choice.correct) || false;
      }

      mark.answered = true;
      if (correct) {
        mark.correct = true;
        this.addChoiceScore(mark, choiceFragment);
        if (choiceFragment.additionalCategories) {
          choiceFragment.additionalCategories?.forEach((additional) =>
            this.addChoiceScore(mark, additional)
          );
        }
        if (choiceFragment.response) {
          mark.responses.push(choiceFragment.response);
        }
      } else {
        if (choiceFragment.response0) {
          mark.responses.push(choiceFragment.response0);
        }
      }

      return mark;
    } else {
      const mm = values.reduce((m, value) => {
        const choice = choices.find(
          (c) => c.oid === value || c.value === value
        );
        if (choice) {
          m.answered = true;
          m.correct = true;
          this.addChoiceScore(mark, choice);
          if (choice.additionalCategories) {
            choice.additionalCategories?.forEach((additional) =>
              this.addChoiceScore(m, additional)
            );
          }
          if (choice.response) {
            m.responses.push(choice.response);
          }
        }
        return m;
      }, mark);

      if (mm.correct && choiceFragment.response) {
        mm.responses.push(choiceFragment.response);
      } else if (choiceFragment.response0) {
        mm.responses.push(choiceFragment.response0);
      }

      return mm;
    }
  }

  addChoiceScore(
    mark: QuizMarkValueLike,
    scoring: { category: string; score: number }
  ) {
    if (scoring.category) {
      const existingCategoryMark = mark.scores.find(
        (s) => s.category === scoring.category
      );
      if (existingCategoryMark) {
        existingCategoryMark.score += scoring.score || 0;
      } else {
        mark.scores.push({
          category: scoring.category,
          score: scoring.score || 0,
          raw: scoring.score || 0,
          ilow: 0,
          ihigh: 0,
        });
      }
    }
    mark.score += scoring.score || 0;
  }

  markQuestionPoints(
    pointsFragment: QuizQuestionPoints,
    answer: AnswerValue
  ): QuizMarkValueLike {
    const mark = {
      qid: answer.qid,
      score: 0,
      scores: [] as QuizMarkScoreLike[],
      responses: [] as QuizResponseLike[],
      answered: false,
      correct: false,
    };

    if (!answer.value || answer.value == '') {
      return mark;
    }

    const options = pointsFragment.options;
    const values: number[] = parsePlusSeparatedNumbers(answer.value as string);
    const answers = options.map((option, index) => values[index] || 0);

    const points = answers.reduce((sum, value) => {
      sum += value;
      return sum;
    }, 0);

    mark.answered = points > 0;
    mark.correct = points >= pointsFragment.min && points <= pointsFragment.max;
    mark.scores = options
      .filter((option) => option.category)
      .map((option, index) => {
        const points = values[index] || 0;
        return {
          category: option.category || '',
          score: points,
          raw: 0,
          ilow: 0,
          ihigh: 0,
        } as QuizMarkScoreLike;
      });
    mark.score = points;

    if (mark.answered) {
      if (pointsFragment.response) {
        mark.responses.push(pointsFragment.response as QuizResponseLike);
      }
    } else {
      if (pointsFragment.response0) {
        mark.responses.push(pointsFragment.response0 as QuizResponseLike);
      }
    }

    return mark;
  }

  markQuestionRank(
    rankFragment: QuizQuestionRank,
    answer: AnswerValue
  ): QuizMarkValueLike {
    const mark = {
      qid: answer.qid,
      score: 0,
      scores: [] as QuizMarkScoreLike[],
      responses: [] as QuizResponseLike[],
      answered: false,
      correct: false,
    };

    if (!answer.value || answer.value == '') {
      return mark;
    }

    const options = rankFragment.options;
    const values: string[] = parseJsonifiedArray(answer.value).filter(
      (answer: string) =>
        options.some(
          (option) => option.oid === answer || option.value === answer
        )
    );

    mark.answered = values.length > 0;
    mark.correct =
      values.length >= rankFragment.min && values.length <= rankFragment.max;
    mark.scores = values.map((value, index) => {
      const option = options.find((option) => option.oid === value);
      const score =
        rankFragment.scoring === 'descending'
          ? values.length - index
          : index + 1;
      return {
        category: option?.category || '',
        score,
        raw: score,
        ilow: 0,
        ihigh: 0,
      };
    });
    mark.score = mark.scores.reduce(
      (total: number, value: QuizMarkScoreLike) => total + value.score,
      0
    );

    if (mark.answered) {
      if (rankFragment.response) {
        mark.responses.push(rankFragment.response);
      }
    } else {
      if (rankFragment.response0) {
        mark.responses.push(rankFragment.response0);
      }
    }

    return mark;
  }

  markQuestion(
    question: QuizQuestion,
    answer: AnswerValue
  ): QuizMarkValueLike | null {
    if (question.qid != answer.qid) {
      throw new Error('Answer to wrong question');
    }

    const type = question.type;
    switch (type) {
      case QuizQuestionType.Text:
        if (!question.text) {
          return null;
        }
        return this.markQuestionText(question.text, answer);
      case QuizQuestionType.Choice:
        if (!question.choice) {
          return null;
        }
        return this.markQuestionChoice(question.choice, answer);
      case QuizQuestionType.Points:
        if (!question.points) {
          return null;
        }
        return this.markQuestionPoints(question.points, answer);
      case QuizQuestionType.Rank:
        if (!question.rank) {
          return null;
        }
        return this.markQuestionRank(question.rank, answer);
      case QuizQuestionType.Meta:
        return this.markQuestionMeta(answer);
      case QuizQuestionType.Hidden:
        return this.markQuestionHidden(answer);
      default:
        return null;
    }
  }

  mark(quiz: Readonly<Quiz>, answers: Readonly<Answer>): QuizMarkLike {
    // Calculate marks
    const allMarks: QuizMarkValueLike[] = (quiz.questions || []).map(
      (question) => {
        const answer = answers.values.find((a) => a.qid == question.qid);
        if (!answer) {
          const mark: QuizMarkValueLike = {
            qid: question.qid,
            score: 0,
            scores: [] as QuizMarkScoreLike[],
            responses: [] as QuizResponseLike[],
            answered: false,
            correct: false,
          };
          return mark;
        }
        const mark = this.markQuestion(question, answer);
        return (
          mark ?? {
            qid: question.qid,
            score: 0,
            scores: [] as QuizMarkScoreLike[],
            responses: [] as QuizResponseLike[],
            answered: false,
            correct: false,
          }
        );
      }
    );

    const filteredQuestions =
      (function () {
        // Need to filter answers?
        if (
          quiz.scoringMethod &&
          quiz.scoringMethod.type === 'weight-categories-by-count'
        ) {
          const weightingQuestion = quiz.questions.find(
            (q) => q.qid === quiz.scoringMethod.qid
          );
          const rankIndex = weightingQuestion
            ? quiz.questions.indexOf(weightingQuestion)
            : -1;
          if (
            weightingQuestion &&
            weightingQuestion.rank!.options.every((item) => item.category)
          ) {
            const weightingAnswers = allMarks.find(
              (m) => m.qid === quiz.scoringMethod.qid
            );
            if (weightingAnswers) {
              const categories: string[] = weightingAnswers.scores.map(
                (s) => s.category
              );
              if (categories.length) {
                return quiz.questions.filter((question, index) => {
                  if (index <= rankIndex) {
                    return true;
                  }

                  if (question.type !== QuizQuestionType.Choice) {
                    return true;
                  }

                  if (question.choice!.scoring === 'individual') {
                    return (question.choice!.choices || []).find((choice) => {
                      return (
                        !choice.category ||
                        categories.some(
                          (cid) =>
                            choice.category === cid ||
                            choice.additionalCategories?.some(
                              (additional) => additional.category === cid
                            )
                        )
                      );
                    });
                  } else {
                    return (
                      !question.choice!.category ||
                      categories.some(
                        (cid) =>
                          question.choice!.category === cid ||
                          question.choice!.additionalCategories?.some(
                            (additional) => additional.category === cid
                          )
                      )
                    );
                  }
                });
              }
            }
          }
        }

        return quiz.questions;
      })() || [];

    const marks = allMarks.map((m) =>
      filteredQuestions.some((q) => q.qid === m.qid)
        ? m
        : {
            qid: m.qid,
            score: 0,
            scores: [] as QuizMarkScoreLike[],
            responses: [] as QuizResponseLike[],
            answered: false,
            correct: false,
          }
    );

    // Ensure mandatory questions have answers
    const validatoryQuestions = filteredQuestions.filter(
      (q) => q.required || q.type === QuizQuestionType.Points
    );
    const invalidQuestions = validatoryQuestions
      .map((q) => {
        if (q.required) {
          if (!marks.some((m) => m.qid === q.qid && m.answered)) {
            return { qid: q.qid, reason: 'required' };
          }
        }
        if (q.type === QuizQuestionType.Points) {
          if (!marks.some((m) => m.qid === q.qid && m.correct)) {
            return { qid: q.qid, reason: 'invalid' };
          }
        }
        if (q.type === QuizQuestionType.Rank) {
          if (!marks.some((m) => m.qid === q.qid && m.correct)) {
            return { qid: q.qid, reason: 'invalid' };
          }
        }
        return null;
      })
      .filter((q) => q);

    if (invalidQuestions.length) {
      // Result
      const failedResult = {
        validation: invalidQuestions,
      } as QuizMarkLike;

      return failedResult;
    }

    const filterMarksForScoring = (mark: QuizMarkValueLike) => {
      if (!quiz.scoringMethod) {
        return true;
      }
      if (quiz.scoringMethod.type === 'weight-categories-by-count') {
        return quiz.scoringMethod.qid !== mark.qid;
      }
      return true;
    };

    // Grand total score
    let grandTotalScore: number = marks
      .filter(filterMarksForScoring)
      .reduce((runningTotal, mark) => {
        runningTotal += mark.score || 0;
        return runningTotal;
      }, 0);

    // Category total scores
    const categoryTotalScores = (quiz.categories || []).map((category) => {
      const scoresForCategory = marks
        .filter(filterMarksForScoring)
        .flatMap((m) => m.scores)
        .filter((m) => m.category === category.cid);

      const categoryTotalScore = scoresForCategory.reduce(
        (runningTotal: number, mark: QuizMarkScoreLike) => {
          runningTotal += mark.score || 0;
          return runningTotal;
        },
        0
      );

      return {
        raw: categoryTotalScore,
        score: categoryTotalScore,
        category: category.cid,
        ilow: 0,
        ihigh: 0,
      };
    });

    // Scoring method - average (mean)
    if (quiz.scoringMethod && quiz.scoringMethod.type === 'mean') {
      quiz.categories.forEach((category) => {
        const questionsUsingThisCategory = filteredQuestions
          .map((q) => {
            switch (q.type) {
              case QuizQuestionType.Choice: {
                switch (q.choice!.scoring) {
                  case 'individual': {
                    if (q.choice!.type === 'multiple') {
                      return q.choice!.choices.filter((c) =>
                        isCategory(c, category.cid)
                      ).length > 0
                        ? 1
                        : 0;
                    } else {
                      return q.choice!.choices.some((c) =>
                        isCategory(c, category.cid)
                      )
                        ? 1
                        : 0;
                    }
                  }
                  case 'none': {
                    return 0;
                  }
                  default:
                  case 'exact': {
                    return isCategory(q.choice!, category.cid) ? 1 : 0;
                  }
                }
              }
              case QuizQuestionType.Points: {
                return (q.points!.options || []).filter((c) =>
                  isCategory(c, category.cid)
                ).length > 0
                  ? 1
                  : 0;
              }
              default: {
                return 0;
              }
            }
          })
          .reduce((sum: number, value: number) => sum + value, 0);

        if (questionsUsingThisCategory > 0) {
          const categoryTotalScore = categoryTotalScores.find((c) =>
            isCategory(c, category.cid)
          );
          if (!categoryTotalScore) {
            return;
          }

          categoryTotalScore.score =
            categoryTotalScore.score / questionsUsingThisCategory;
        }
      });

      // TODO: calculate additional categories
      const questionsUsingTotal = filteredQuestions
        .map((q) => {
          switch (q.type) {
            case QuizQuestionType.Choice: {
              switch (q.choice!.scoring) {
                case 'individual': {
                  if (q.choice!.type === 'multiple') {
                    return q.choice!.choices.filter(
                      (c) => !c.category && c.score > 0
                    )
                      ? 1
                      : 0;
                  } else {
                    return q.choice!.choices.some(
                      (c) => !c.category && c.score > 0
                    )
                      ? 1
                      : 0;
                  }
                }
                case 'none': {
                  return 0;
                }
                default:
                case 'exact': {
                  return !q.choice!.category && q.choice!.score > 0 ? 1 : 0;
                }
              }
            }
            case QuizQuestionType.Points: {
              return (q.points!.options || []).filter((c) => !c.category)
                .length > 0
                ? 1
                : 0;
            }
            default: {
              return 0;
            }
          }
        })
        .reduce((sum: number, value: number) => sum + value, 0);

      if (questionsUsingTotal > 0) {
        grandTotalScore = grandTotalScore / questionsUsingTotal;
      }
    }

    if (
      quiz.scoringMethod &&
      quiz.scoringMethod.type === 'weight-categories-by-count'
    ) {
      const weightingQuestion = quiz.questions.find(
        (q) => q.qid === quiz.scoringMethod.qid
      );
      const weightingAnswers = marks.find(
        (m) => m.qid === quiz.scoringMethod.qid
      );

      // Calculate weighted percentages for categories
      const weightedPercentages = quiz.categories
        .map((category) => {
          const cid = category.cid;
          const parentCid = category.parent || '';

          const questionsUsingThisCategory = filteredQuestions
            .filter((q) => q.qid !== quiz.scoringMethod.qid)
            .map((q) => {
              switch (q.type) {
                case QuizQuestionType.Text: {
                  return q.text!.category === cid ? 1 : 0;
                }
                case QuizQuestionType.Choice: {
                  switch (q.choice!.scoring) {
                    case 'individual': {
                      if (q.choice!.type === 'multiple') {
                        return q.choice!.choices.filter((c) =>
                          isCategory(c, cid)
                        ).length;
                      } else {
                        return q.choice!.choices.some((c) => isCategory(c, cid))
                          ? 1
                          : 0;
                      }
                    }
                    case 'none': {
                      return 0;
                    }
                    default:
                    case 'exact': {
                      return isCategory(q.choice!, cid) ? 1 : 0;
                    }
                  }
                }
                case QuizQuestionType.Points: {
                  return q.points!.options.filter((c) => isCategory(c, cid))
                    .length;
                }
                case QuizQuestionType.Rank: {
                  return Math.max(
                    q.rank!.max,
                    q.rank!.options.filter((c) => isCategory(c, cid)).length
                  );
                }
                default: {
                  return 0;
                }
              }
            })
            .reduce((sum, value) => sum + value, 0);

          if (weightingQuestion) {
            const weightOptions =
              (function () {
                switch (weightingQuestion.type) {
                  case QuizQuestionType.Choice:
                    return weightingQuestion.choice!.choices;
                  case QuizQuestionType.Rank:
                    return weightingQuestion.rank!.options;
                  default:
                    return [];
                }
              })() || [];
            const maxWeighting = weightOptions.filter((option) =>
              isCategoryOrParentCategory(option, cid, parentCid)
            ).length;
            if (weightingAnswers && weightingAnswers.scores) {
              const userWeighting = weightingAnswers.scores.filter((score) =>
                isCategoryOrParentCategory(score, cid, parentCid)
              ).length;
              return {
                category,
                maxWeighting,
                userWeighting,
                questionsUsingThisCategory,
              };
            } else {
              return {
                category,
                maxWeighting,
                userWeighting: 0,
                questionsUsingThisCategory,
              };
            }
          } else {
            return {
              category,
              maxWeighting: 1,
              userWeighting: 1,
              questionsUsingThisCategory,
            };
          }
        })
        .filter((wp) => wp.maxWeighting > 0);

      weightedPercentages.forEach((wp) => {
        const categoryTotalScore = categoryTotalScores.find(
          (c) => c.category === wp.category.cid
        );
        if (!categoryTotalScore) {
          return;
        }

        if (
          wp.userWeighting > 0 &&
          wp.questionsUsingThisCategory === 0 &&
          !wp.category.parent
        ) {
          // No questions use this category - calculate score from sum of other categories
          const weightedPercentagesInUse = weightedPercentages.filter(
            (wp) => wp.questionsUsingThisCategory > 0
          );
          const categoryTotalScoresInUse = categoryTotalScores.filter((c) =>
            weightedPercentagesInUse.some(
              (wp) => wp.category.cid === c.category
            )
          );
          const score = categoryTotalScoresInUse.reduce(
            (sum: number, cts: { score: number }) => sum + cts.score,
            0
          );
          categoryTotalScore.score = score;
          wp.questionsUsingThisCategory = weightedPercentagesInUse.reduce(
            (sum, wp) => sum + wp.questionsUsingThisCategory,
            0
          );
        }
      });

      weightedPercentages.forEach((wp) => {
        const categoryTotalScore = categoryTotalScores.find(
          (c) => c.category === wp.category.cid
        );
        if (!categoryTotalScore) {
          return;
        }

        if (wp.questionsUsingThisCategory > 0) {
          // At least one question uses this category
          const scalingFactor =
            wp.userWeighting / wp.maxWeighting / wp.questionsUsingThisCategory;
          categoryTotalScore.score = categoryTotalScore.score * scalingFactor;
        }
      });
    }

    // Apply error bars
    categoryTotalScores.forEach((total) => {
      const category = quiz.categories.find((c) => c.cid === total.category);
      if (!category || !category.mappings || !category.mappings.length) {
        return;
      }
    });

    // Apply lookup tables and error bars
    categoryTotalScores.forEach((total) => {
      const category = quiz.categories.find((c) => c.cid === total.category);
      if (!category) {
        return;
      }

      const applyMapping = (score: number) => {
        const mapping =
          category.mappings && category.mappings.length
            ? category.mappings.find((m) => m.score === Math.round(score))
            : null;
        return mapping ? mapping.value : score;
      };

      const applyErrorBars = category.sd > 0 && category.ic > 0;

      if (applyErrorBars) {
        const standardDeviation = category.sd;
        const internalConsistency = category.ic;
        const range = Math.sqrt(1 - internalConsistency) * standardDeviation;

        total.ilow = applyMapping(total.score - range);
        total.ihigh = applyMapping(total.score + range);
      }

      total.score = applyMapping(total.score);
    });

    // Grand total responses
    const totals = quiz.totals.filter(
      (f) =>
        !f.category &&
        grandTotalScore >= f.from &&
        grandTotalScore <= (f.to || 1000000)
    );

    // Category total responses
    const categoryTotals = (quiz.categories || [])
      .map((category) => {
        const categoryScore = (
          categoryTotalScores.find((t) => t.category === category.cid) || {
            score: 0,
          }
        ).score;
        return {
          total: quiz.totals.filter(
            (f) =>
              f.category === category.cid &&
              categoryScore >= f.from &&
              categoryScore <= (f.to || 1000000)
          ),
          score: categoryScore,
        };
      })
      .map((t) => t.total);

    // Result
    const result = {
      score: grandTotalScore,
      scores: categoryTotalScores,
      marks: marks,
      totals: [...totals, ...categoryTotals.flat()].filter((total) => {
        if (!total.qid) {
          return true;
        }
        const question = quiz.questions.find((q) => q.qid === total.qid);
        const answer = (answers.values || []).find((a) => a.qid === total.qid);
        if (!question || !answer) {
          return false;
        }
        if (!total.answer) {
          return !answer.value;
        } else {
          switch (question.type) {
            case QuizQuestionType.Choice: {
              const choice =
                question && question.choice && question.choice.choices
                  ? question.choice.choices.find(
                      (c) => c.oid === total.answer || c.value === total.answer
                    )
                  : null;
              return (
                choice &&
                (answer.value === choice.oid || answer.value === choice.value)
              );
            }
            default:
              return total.answer === answer.value;
          }
        }
      }),
      validation: [],
    };

    return result;
  }
}

export default new Marker();
